import React, { useRef, useState } from 'react';
import Head from 'next/head';
import { Box, Grid } from '@mui/material';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import {
  PermissionsGate,
  AccidentForm,
  TabsV2,
  CommentsV2,
  ActivityListV2,
  TaskCarDetails,
  TaskDetailScrollableSection,
  MoreButton,
} from '@components';

import { DrawerLayout, TaskLayout } from '@layouts';
import { useArnmAccidentsTaskOperations, usePermissions } from '@hooks';

import { showError } from '@utils';
import { boardsServices } from '@services';
import {
  UploadARNMImageV2,
  UploadARNMDocumentV2,
} from 'components/common/media';
import {
  ARNM_ACCIDENTS_TASK_FIELD_NAMES,
  ARNM_ACCIDENTS_TASK_STATUSES_NAMES,
} from 'components/arnmAccidents/constants';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { ButtonV2 } from 'components/common/ui';
import { TaskSection } from 'components/common/TaskSection';

export const tabs = [
  {
    label: 'Ticket Information',
    id: 'ticket-details',
    icon: <InfoIcon data-testid="InfoIcon" />,
  },
  {
    label: 'Work Order',
    id: 'work-order',
    icon: <BuildIcon data-testid="BuildIcon" />,
  },
  {
    label: 'Financial Details',
    id: 'financial-details',
    icon: <EuroSymbolIcon data-testid="EuroSymbolIcon" />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <ChatBubbleIcon data-testid="ChatBubbleIcon" />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <HistoryIcon data-testid="HistoryIcon" />,
  },
];

export const rightTabs = [
  {
    label: 'Ticket Details',
    icon: <FormatListBulletedIcon data-testid="FormatListBulletedIcon" />,
    id: 'ticket-details-right',
  },
  {
    label: 'Car Details',
    icon: <DirectionsCarIcon data-testid="DirectionsCarIcon" />,
    id: 'car-details',
  },
  {
    label: 'Files',
    icon: <InsertDriveFileIcon data-testid="InsertDriveFileIcon" />,
    id: 'documents',
  },
  {
    label: 'Images',
    icon: <InsertPhotoIcon data-testid="InsertPhotoIcon" />,
    id: 'images',
  },
];
const ArnmAccidentsTask = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    task,
    vehicle,
    taskId,
    loading,
    assignees,
    arnmAssignees,
    servicePoints,
    insurances,
    stages,
    getArnmAccidentsTask,
    updateTask,
  } = useArnmAccidentsTaskOperations();
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const submitBtnRef = useRef();

  const { findEntity } = usePermissions();

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      await updateTask({ values, images, documents });
      await getArnmAccidentsTask();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddComment = async (comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we save the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.addTaskComment(taskId, comment);

      await getArnmAccidentsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we edit the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.editTaskComment(taskId, commentId, comment);
      await getArnmAccidentsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we delete the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.deleteTaskComment(taskId, commentId);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      setIsSubmitting(true);
      await boardsServices.deleteTaskImage(taskId, imageId);
      await getArnmAccidentsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      setIsSubmitting(true);
      await boardsServices.deleteTaskDocument(taskId, docId);
      await getArnmAccidentsTask();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeCarDetails = async ({ field, value }) => {
    const payload = {
      [field]: value,
    };

    try {
      setIsSubmitting(true);
      await boardsServices.updateTask(taskId, payload);

      await getArnmAccidentsTask();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStage = async (id) => {
    const payload = {
      status: Number(id),
    };
    try {
      setIsSubmitting(true);
      await submitBtnRef?.current?.click?.();
      await boardsServices.updateTask(taskId, payload);

      await getArnmAccidentsTask();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeaderOptions = () => {
    return (
      <PermissionsGate entity={ENTITIES_PERMISIONS.WriteAccidentsClaims}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MoreButton
            moreOptions={[
              {
                title: 'Cancel Ticket',
                onClick: () => handleChangeStage(12),
                hidden: !findEntity([ENTITIES_PERMISIONS.WriteAccidentsClaims]),
              },
            ]}
          />
          <ButtonV2
            ref={submitBtnRef}
            type="submit"
            size="small"
            form="armaccident-form"
            disabled={loading || isSubmitting}
          >
            Save
          </ButtonV2>
        </Box>
      </PermissionsGate>
    );
  };

  const taskStatusNames = Object.keys(
    ARNM_ACCIDENTS_TASK_STATUSES_NAMES
  ).reduce((acc, cur) => {
    acc[cur] = ARNM_ACCIDENTS_TASK_STATUSES_NAMES[cur].label;

    return acc;
  }, {});

  return (
    <>
      <Head>
        <title>{`Accidents/Claims: ${taskId} `}</title>
        <meta name="description" content="instafleet" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={taskId}
        entity={ENTITIES_PERMISIONS.ReadAccidentsClaims}
        renderHeaderOptions={renderHeaderOptions}
        isLoading={loading || isSubmitting}
        stages={stages}
        currentStage={task?.status?.value}
        onChangeStage={handleChangeStage}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="armaccident-task-details" />

            <TaskDetailScrollableSection id="armaccident-task-details">
              <AccidentForm
                isChargeClient={task?.status?.value === 17}
                onSubmit={handleSubmit}
                defaultValues={task}
                loading={loading}
                assignees={assignees}
                servicePoints={servicePoints}
                insurances={insurances}
              />

              <TaskSection
                title={tabs[3].label}
                id={tabs[3].id}
                icon={tabs[3].icon}
              >
                <CommentsV2
                  comments={task?.comments}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  users={assignees}
                />
              </TaskSection>
              <TaskSection
                title={tabs[4].label}
                id={tabs[4].id}
                icon={tabs[4].icon}
              >
                <ActivityListV2
                  activities={task?.changelog}
                  taskFieldNames={ARNM_ACCIDENTS_TASK_FIELD_NAMES}
                  taskStatusNames={taskStatusNames}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2 tabs={rightTabs} observerRootId="armaccident-task-right" />
            <TaskDetailScrollableSection id="armaccident-task-right">
              <TaskSection
                isSmall
                title="Ticket Details"
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
              >
                <TaskCarDetails
                  task={task}
                  assignees={arnmAssignees}
                  stages={stages}
                  onChange={handleChangeCarDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[1].label}
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <TaskCarDetails
                  vehicle={vehicle}
                  onChange={handleChangeCarDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[2].label}
                id={rightTabs[2].id}
                icon={rightTabs[2].icon}
              >
                <UploadARNMDocumentV2
                  documents={task?.documents}
                  onChange={setDocuments}
                  onDeleteDocument={handleDeleteDocument}
                  loading={loading || isSubmitting}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title={rightTabs[3].label}
                id={rightTabs[3].id}
                icon={rightTabs[3].icon}
              >
                <UploadARNMImageV2
                  images={task?.images}
                  onChange={setImages}
                  onDeleteImage={handleDeleteImage}
                  loading={loading || isSubmitting}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

ArnmAccidentsTask.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default ArnmAccidentsTask;
